import { useAuthStore } from '@/stores/auth-store';
import { logger } from '@/lib/logger';
import { refreshSession } from './refreshSession';

/**
 * Renew this long before the token expires. Wide enough to cover a slow
 * network and a suspended tab that wakes up late, narrow enough that the
 * short token stays short.
 */
const MAX_RENEW_LEAD_MS = 5 * 60 * 1000;

/**
 * Ceiling on the lead as a share of the token's own lifetime. Without it, a
 * token shorter than the lead would be renewed the instant it arrives — and
 * its replacement too, spinning one request per second. That is not
 * hypothetical: JWT_ACCESS_TOKEN_TTL_SECONDS is turned down to seconds to
 * exercise this very path.
 */
const MAX_RENEW_LEAD_SHARE = 0.2;

/**
 * Floor on any scheduled delay.
 *
 * A client clock running ahead of the server by more than the token lifetime
 * makes every freshly minted token look already-expired; without a floor the
 * schedule would renew, get another "expired" token, and renew again — one
 * identity-provider rotation per second until the rate limiter cut it off.
 * Thirty seconds turns that pathological case into a slow, survivable loop.
 */
const MIN_RENEW_DELAY_MS = 30_000;

/**
 * setTimeout takes a 32-bit signed delay; anything larger wraps and fires
 * immediately, turning a very long token into the same hot loop.
 */
const MAX_TIMER_MS = 2 ** 31 - 1;

/** Retry cadence after a renewal that could not reach a verdict. */
const RETRY_DELAY_MS = 30_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let scheduledFor: string | null = null;
let subscribed = false;

/**
 * Renews the session shortly BEFORE its token expires, so an active user
 * never waits for a failed request to be replayed.
 *
 * The 401 handler in agent.ts is the safety net and works on its own; this is
 * what makes renewal invisible. Both are needed: a tab that sat idle past the
 * expiry has no valid timer left to fire (browsers throttle background
 * timers), and its next request takes the reactive path.
 *
 * Driven by a store subscription rather than called from setAuth, so the auth
 * store stays free of any dependency on the network layer.
 */
export function startRenewalSchedule(): void {
  reschedule(useAuthStore.getState().token);
  // Idempotent: StrictMode invokes the caller twice, and two subscriptions
  // would renew twice on every token change.
  if (subscribed) return;
  subscribed = true;
  useAuthStore.subscribe((state) => reschedule(state.token));
}

function reschedule(token: string): void {
  // Same token, timer already running: a store update that did not touch the
  // session (a workspace-switch flag, say) must not restart the clock.
  if (token && token === scheduledFor) return;

  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  scheduledFor = token || null;
  if (!token) return;

  const expiresAt = readExpiry(token);
  if (expiresAt === null) return;

  arm(token, renewalDelay(expiresAt));
}

function arm(token: string, delay: number): void {
  timer = setTimeout(() => {
    timer = null;
    void refreshSession().then((outcome) => {
      if (outcome === 'renewed') return;
      // 'dead' needs nothing here — the next request's 401 handler owns the
      // teardown. 'unavailable' must be retried, though: without it one lost
      // packet leaves the session with no proactive schedule at all (the
      // token is unchanged, so the store never fires again), and an idle
      // browser silently rides its idle cap out to a hard re-login.
      if (outcome === 'unavailable' && scheduledFor === token) {
        logger.warn('🔁 Session renewal unavailable - retrying shortly');
        arm(token, RETRY_DELAY_MS);
      }
    });
  }, delay);
}

/** How long to wait before renewing a token that expires at `expiresAt`. */
function renewalDelay(expiresAt: number): number {
  const remaining = expiresAt - Date.now();
  const lead = Math.min(MAX_RENEW_LEAD_MS, remaining * MAX_RENEW_LEAD_SHARE);
  return clamp(remaining - lead, MIN_RENEW_DELAY_MS, MAX_TIMER_MS);
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Reads `exp` (epoch seconds) out of the JWT payload. Deliberately unverified
 * — the client cannot check a signature it has no key for, and it does not
 * need to: this value only decides WHEN to ask the server to renew, and the
 * server verifies everything. Answers null for anything unreadable, which
 * simply leaves the reactive 401 path in charge.
 */
function readExpiry(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    const exp =
      typeof claims === 'object' && claims !== null ? (claims as { exp?: unknown }).exp : undefined;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}
